import {
  useMemo,
  useSyncExternalStore,
} from 'react';

import {
  getGovernorRuntimeEvidenceRevision,
  getGovernorRuntimeEvidenceSnapshot,
  subscribeGovernorRuntimeEvidence,
} from '@/governor/runtime-evidence/GovernorRuntimeEvidenceBus';

import {
  projectTreeRuntimeConvergenceEvidence,
  type TreeRuntimeConvergenceLiveProjection,
} from '@/governor/tree-runtime/TreeRuntimeConvergenceEvidenceProjection';

import {
  TREE_GEOMETRY_FAILURE_SOURCE_SCHEMA,
  TREE_GEOMETRY_RUNTIME_FATAL_RULE_KEYS,
  type TreeGeometryRuntimeFatalRuleKey,
} from '@/governor/treeGeometryEvidence';

import {
  readTreeRuntimeConvergenceSessionEvidence,
} from '@/governor/tree-runtime/TreeRuntimeConvergenceSessionEvidence';

import {
  LEGACY_ANCESTRY_HORIZONTAL_RULES,
  TREE_RUNTIME_LEGACY_ANCESTRY_CAUSAL_SCHEMA,
  type LegacyAncestryHorizontalRule,
} from '@/governor/tree-runtime/TreeRuntimeLegacyAncestryResidueCausalLocalization';

import {
  TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA,
  type TreeRuntimeCanonicalBranchCounterfactualRuleCounts,
} from '@/governor/tree-runtime/TreeRuntimeCanonicalBranchCounterfactualShadowProjection';

import {
  TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_COMPARISON_SCHEMA,
} from '@/governor/tree-runtime/TreeRuntimeBranchLayoutInteractionCounterfactualComparison';

import {
  CANONICAL_TREE_VIEWER_TERMINAL_FAILURE_KEYS,
  type CanonicalTreeViewerTerminalFailureKey,
} from '@/governor/tree/CanonicalTreeConnectorCorridorAuthority';


const getServerRevision =
  (): number => 0;

export const TREE_GEOMETRY_RUNTIME_MAX_AGE_MS =
  10 * 60 * 1000;

export type TreeGeometryRuntimeEvidenceAvailability =
  | 'NOT_OBSERVED'
  | 'OBSERVED'
  | 'INVALID_EVIDENCE';

export type TreeGeometryFailureSourceAvailability =
  | 'NOT_PUBLISHED'
  | 'OBSERVED'
  | 'INVALID_EVIDENCE';

export interface TreeGeometryRuntimeLiveProjection {
  readonly availability:
    TreeGeometryRuntimeEvidenceAvailability;

  readonly status:
    'PASS' | 'FAIL' | null;

  readonly failureSourceClassificationAvailability:
    TreeGeometryFailureSourceAvailability;

  readonly observedAt:
    string | null;

  readonly issueCount:
    number | null;

  readonly fatalIssueCount:
    number | null;

  readonly warningIssueCount:
    number | null;

  readonly fatalKnownIssueCount:
    number | null;

  readonly fatalUnknownIssueCount:
    number | null;

  readonly informationalIssueCount:
    number | null;

  readonly passIssueCount:
    number | null;

  readonly unclassifiedSeverityCount:
    number | null;

  readonly routeRejectedNonadjacentGenerationCount:
    number | null;

  readonly routeRejectedRootBoundaryCount:
    number | null;

  readonly routeRejectedObstacleIntersectionCount:
    number | null;

  readonly routeRejectedUnclassifiedCount:
    number | null;

  readonly fatalRuleCounts:
    Readonly<
      Record<
        TreeGeometryRuntimeFatalRuleKey,
        number
      >
    > | null;

  readonly cardCount:
    number | null;

  readonly edgeCount:
    number | null;

  readonly parentConnectorMissingCount:
    number | null;

  readonly parentConnectorUnroutedCount:
    number | null;

  readonly routeRejectedCount:
    number | null;

  readonly routeMissingCount:
    number | null;

  readonly parentEndpointAuthorityFailureCount:
    number | null;

  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly identitySuppressed: true;
}

const emptyTreeGeometryRuntimeEvidence =
  (): TreeGeometryRuntimeLiveProjection =>
    Object.freeze({
      availability:
        'NOT_OBSERVED',
      status: null,
      failureSourceClassificationAvailability:
        'NOT_PUBLISHED',
      observedAt: null,
      issueCount: null,
      fatalIssueCount: null,
      warningIssueCount: null,

      fatalKnownIssueCount: null,
      fatalUnknownIssueCount: null,
      informationalIssueCount: null,
      passIssueCount: null,
      unclassifiedSeverityCount: null,

      routeRejectedNonadjacentGenerationCount:
        null,
      routeRejectedRootBoundaryCount:
        null,
      routeRejectedObstacleIntersectionCount:
        null,
      routeRejectedUnclassifiedCount:
        null,

      fatalRuleCounts: null,

      cardCount: null,
      edgeCount: null,
      parentConnectorMissingCount:
        null,
      parentConnectorUnroutedCount:
        null,
      routeRejectedCount: null,
      routeMissingCount: null,
      parentEndpointAuthorityFailureCount:
        null,
      readOnly: true,
      repairExecuted: false,
      identitySuppressed: true,
    });

const evidenceValue = (
  evidence:
    readonly string[],
  key:
    string,
): string | null => {
  const prefix =
    `${key}=`;

  const match =
    evidence.find(
      item =>
        item.startsWith(
          prefix,
        ),
    );

  return match
    ? match.slice(
        prefix.length,
      )
    : null;
};

const evidenceCount = (
  evidence:
    readonly string[],
  key:
    string,
): number | null => {
  const raw =
    evidenceValue(
      evidence,
      key,
    );

  if (raw === null) {
    return null;
  }

  const value =
    Number(raw);

  return (
    Number.isInteger(value) &&
    value >= 0 &&
    value <= 1_000_000
  )
    ? value
    : null;
};

const evidenceBoolean = (
  evidence:
    readonly string[],
  key:
    string,
): boolean | null => {
  const raw =
    evidenceValue(
      evidence,
      key,
    );

  if (raw === 'true') {
    return true;
  }

  if (raw === 'false') {
    return false;
  }

  return null;
};

const evidenceOccurrenceCount = (
  evidence:
    readonly string[],
  key:
    string,
): number => {
  const prefix =
    `${key}=`;

  return evidence.filter(
    value =>
      value.startsWith(
        prefix,
      ),
  ).length;
};


export const projectTreeGeometryRuntimeEvidence =
  (
    snapshot:
      ReturnType<
        typeof getGovernorRuntimeEvidenceSnapshot
      >,
    nowMs:
      number = Date.now(),
  ): TreeGeometryRuntimeLiveProjection => {
    const event =
      [...snapshot.events]
        .reverse()
        .find(
          candidate =>
            candidate.domain ===
              'tree' &&
            candidate.source ===
              'TreeGeometryEvidence',
        );

    if (!event) {
      return (
        emptyTreeGeometryRuntimeEvidence()
      );
    }

    const evidence =
      event.evidence;

    const authenticatedSession =
      evidenceValue(
        evidence,
        'authenticatedSession',
      ) === 'true';

    const eventTimestampMs =
      Date.parse(
        event.timestamp,
      );

    const eventAgeMs =
      nowMs -
      eventTimestampMs;

    const statusRaw =
      evidenceValue(
        evidence,
        'status',
      );

    const status =
      statusRaw === 'PASS' ||
      statusRaw === 'FAIL'
        ? statusRaw
        : null;

    const baseCounts = {
      issueCount:
        evidenceCount(
          evidence,
          'issueCount',
        ),

      fatalIssueCount:
        evidenceCount(
          evidence,
          'fatalIssueCount',
        ),

      warningIssueCount:
        evidenceCount(
          evidence,
          'warningIssueCount',
        ),

      cardCount:
        evidenceCount(
          evidence,
          'cardCount',
        ),

      edgeCount:
        evidenceCount(
          evidence,
          'edgeCount',
        ),

      parentConnectorMissingCount:
        evidenceCount(
          evidence,
          'parentConnectorMissingCount',
        ),

      parentConnectorUnroutedCount:
        evidenceCount(
          evidence,
          'parentConnectorUnroutedCount',
        ),

      routeRejectedCount:
        evidenceCount(
          evidence,
          'routeRejectedCount',
        ),

      routeMissingCount:
        evidenceCount(
          evidence,
          'routeMissingCount',
        ),

      parentEndpointAuthorityFailureCount:
        evidenceCount(
          evidence,
          'parentEndpointAuthorityFailureCount',
        ),
    };

    const failureSourceSchema =
      evidenceValue(
        evidence,
        'failureSourceSchema',
      );

    const failureSourceScalarKeys = [
      'fatalKnownIssueCount',
      'fatalUnknownIssueCount',
      'informationalIssueCount',
      'passIssueCount',
      'unclassifiedSeverityCount',
      'routeRejectedNonadjacentGenerationCount',
      'routeRejectedRootBoundaryCount',
      'routeRejectedObstacleIntersectionCount',
      'routeRejectedUnclassifiedCount',
    ] as const;

    const hasFailureSourceTokens =
      evidence.some(
        value =>
          failureSourceScalarKeys.some(
            key =>
              value.startsWith(
                `${key}=`,
              ),
          ) ||
          value.startsWith(
            'fatalRule.',
          ),
      );

    const failureSourceAttempted =
      failureSourceSchema !== null ||
      hasFailureSourceTokens;

    const invalidProjection =
      (
        failureSourceInvalid:
          boolean,
      ): TreeGeometryRuntimeLiveProjection =>
        Object.freeze({
          ...emptyTreeGeometryRuntimeEvidence(),
          availability:
            'INVALID_EVIDENCE',
          failureSourceClassificationAvailability:
            failureSourceInvalid
              ? 'INVALID_EVIDENCE'
              : 'NOT_PUBLISHED',
          observedAt:
            event.timestamp,
        });

    const baseValid =
      authenticatedSession &&
      Number.isFinite(
        eventTimestampMs,
      ) &&
      eventAgeMs >= 0 &&
      status !== null &&
      Object.values(
        baseCounts,
      ).every(
        value =>
          value !== null,
      );

    if (!baseValid) {
      return invalidProjection(
        failureSourceAttempted,
      );
    }

    if (
      eventAgeMs >
      TREE_GEOMETRY_RUNTIME_MAX_AGE_MS
    ) {
      return (
        emptyTreeGeometryRuntimeEvidence()
      );
    }

    /*
     * Historical 003G3E compatibility.
     *
     * A complete pre-003G3E1 geometry event remains valid
     * geometry evidence. It does not acquire failure-source
     * classification authority merely because the new
     * projector can understand that schema.
     */
    if (
      failureSourceSchema === null &&
      !hasFailureSourceTokens
    ) {
      return Object.freeze({
        availability:
          'OBSERVED',
        status,
        failureSourceClassificationAvailability:
          'NOT_PUBLISHED',
        observedAt:
          event.timestamp,

        ...baseCounts,

        fatalKnownIssueCount: null,
        fatalUnknownIssueCount: null,
        informationalIssueCount: null,
        passIssueCount: null,
        unclassifiedSeverityCount: null,

        routeRejectedNonadjacentGenerationCount:
          null,
        routeRejectedRootBoundaryCount:
          null,
        routeRejectedObstacleIntersectionCount:
          null,
        routeRejectedUnclassifiedCount:
          null,

        fatalRuleCounts: null,

        readOnly: true,
        repairExecuted: false,
        identitySuppressed: true,
      });
    }

    /*
     * Classification tokens without the explicit 003G3E1
     * schema marker are ambiguous and fail closed.
     */
    if (
      failureSourceSchema === null ||
      failureSourceSchema !==
        TREE_GEOMETRY_FAILURE_SOURCE_SCHEMA
    ) {
      return invalidProjection(
        true,
      );
    }

    const classificationCounts = {
      fatalKnownIssueCount:
        evidenceCount(
          evidence,
          'fatalKnownIssueCount',
        ),

      fatalUnknownIssueCount:
        evidenceCount(
          evidence,
          'fatalUnknownIssueCount',
        ),

      informationalIssueCount:
        evidenceCount(
          evidence,
          'informationalIssueCount',
        ),

      passIssueCount:
        evidenceCount(
          evidence,
          'passIssueCount',
        ),

      unclassifiedSeverityCount:
        evidenceCount(
          evidence,
          'unclassifiedSeverityCount',
        ),

      routeRejectedNonadjacentGenerationCount:
        evidenceCount(
          evidence,
          'routeRejectedNonadjacentGenerationCount',
        ),

      routeRejectedRootBoundaryCount:
        evidenceCount(
          evidence,
          'routeRejectedRootBoundaryCount',
        ),

      routeRejectedObstacleIntersectionCount:
        evidenceCount(
          evidence,
          'routeRejectedObstacleIntersectionCount',
        ),

      routeRejectedUnclassifiedCount:
        evidenceCount(
          evidence,
          'routeRejectedUnclassifiedCount',
        ),
    };

    const fatalRuleCounts =
      Object.fromEntries(
        TREE_GEOMETRY_RUNTIME_FATAL_RULE_KEYS.map(
          rule => [
            rule,
            evidenceCount(
              evidence,
              `fatalRule.${rule}`,
            ),
          ],
        ),
      ) as Record<
        TreeGeometryRuntimeFatalRuleKey,
        number | null
      >;

    const occurrenceCount =
      (
        key:
          string,
      ): number => {
        const prefix =
          `${key}=`;

        return evidence.filter(
          value =>
            value.startsWith(
              prefix,
            ),
        ).length;
      };

    const baseKeys = [
      'authenticatedSession',
      'status',
      'issueCount',
      'fatalIssueCount',
      'warningIssueCount',
      'cardCount',
      'edgeCount',
      'parentConnectorMissingCount',
      'parentConnectorUnroutedCount',
      'routeRejectedCount',
      'routeMissingCount',
      'parentEndpointAuthorityFailureCount',
    ] as const;

    const uniqueScalarEvidence =
      occurrenceCount(
        'failureSourceSchema',
      ) === 1 &&
      baseKeys.every(
        key =>
          occurrenceCount(
            key,
          ) === 1,
      ) &&
      failureSourceScalarKeys.every(
        key =>
          occurrenceCount(
            key,
          ) === 1,
      );

    const uniqueFatalRuleEvidence =
      TREE_GEOMETRY_RUNTIME_FATAL_RULE_KEYS.every(
        rule =>
          occurrenceCount(
            `fatalRule.${rule}`,
          ) === 1,
      );

    const fatalRuleValues =
      TREE_GEOMETRY_RUNTIME_FATAL_RULE_KEYS.map(
        rule =>
          fatalRuleCounts[rule],
      );

    const fatalRuleTotal =
      fatalRuleValues.reduce(
        (total, value) =>
          total + (value ?? 0),
        0,
      );

    const fatalParityValid =
      classificationCounts
        .fatalKnownIssueCount !==
        null &&
      classificationCounts
        .fatalUnknownIssueCount !==
        null &&
      baseCounts.fatalIssueCount !==
        null &&
      classificationCounts
          .fatalKnownIssueCount +
        classificationCounts
          .fatalUnknownIssueCount ===
        baseCounts.fatalIssueCount &&
      fatalRuleTotal ===
        classificationCounts
          .fatalKnownIssueCount;

    const routeParityValid =
      baseCounts.routeRejectedCount !==
        null &&
      classificationCounts
        .routeRejectedNonadjacentGenerationCount !==
        null &&
      classificationCounts
        .routeRejectedRootBoundaryCount !==
        null &&
      classificationCounts
        .routeRejectedObstacleIntersectionCount !==
        null &&
      classificationCounts
        .routeRejectedUnclassifiedCount !==
        null &&
      classificationCounts
          .routeRejectedNonadjacentGenerationCount +
        classificationCounts
          .routeRejectedRootBoundaryCount +
        classificationCounts
          .routeRejectedObstacleIntersectionCount +
        classificationCounts
          .routeRejectedUnclassifiedCount ===
        baseCounts.routeRejectedCount;

    const issueParityValid =
      baseCounts.issueCount !== null &&
      baseCounts.fatalIssueCount !== null &&
      baseCounts.warningIssueCount !== null &&
      classificationCounts
        .informationalIssueCount !==
        null &&
      classificationCounts
        .passIssueCount !== null &&
      classificationCounts
        .unclassifiedSeverityCount !==
        null &&
      baseCounts.fatalIssueCount +
        baseCounts.warningIssueCount +
        classificationCounts
          .informationalIssueCount +
        classificationCounts
          .passIssueCount +
        classificationCounts
          .unclassifiedSeverityCount ===
        baseCounts.issueCount;

    const duplicatedAggregateParityValid =
      fatalRuleCounts[
        'GEOMETRY-PARENT-CONNECTOR-MISSING'
      ] ===
        baseCounts.parentConnectorMissingCount &&
      fatalRuleCounts[
        'GEOMETRY-PARENT-CONNECTOR-UNROUTED'
      ] ===
        baseCounts.parentConnectorUnroutedCount &&
      fatalRuleCounts[
        'GEOMETRY-CONNECTOR-ROUTE-REJECTED'
      ] ===
        baseCounts.routeRejectedCount &&
      fatalRuleCounts[
        'GEOMETRY-CONNECTOR-ROUTE-MISSING'
      ] ===
        baseCounts.routeMissingCount &&
      fatalRuleCounts[
        'GEOMETRY-PARENT-ENDPOINT-AUTHORITY'
      ] ===
        baseCounts
          .parentEndpointAuthorityFailureCount;

    const statusParityValid =
      baseCounts.fatalIssueCount !==
        null &&
      classificationCounts
        .passIssueCount !== null &&
      (
        status === 'FAIL'
          ? (
              baseCounts.fatalIssueCount > 0 &&
              classificationCounts
                .passIssueCount === 0
            )
          : (
              baseCounts.fatalIssueCount === 0 &&
              classificationCounts
                .passIssueCount === 1
            )
      );

    const severityVocabularyValid =
      classificationCounts
        .unclassifiedSeverityCount === 0;

    const classificationValid =
      uniqueScalarEvidence &&
      uniqueFatalRuleEvidence &&
      Object.values(
        classificationCounts,
      ).every(
        value =>
          value !== null,
      ) &&
      fatalRuleValues.every(
        value =>
          value !== null,
      ) &&
      fatalParityValid &&
      routeParityValid &&
      issueParityValid &&
      duplicatedAggregateParityValid &&
      statusParityValid &&
      severityVocabularyValid;

    if (!classificationValid) {
      return invalidProjection(
        true,
      );
    }

    const normalizedFatalRuleCounts =
      Object.fromEntries(
        TREE_GEOMETRY_RUNTIME_FATAL_RULE_KEYS.map(
          rule => [
            rule,
            fatalRuleCounts[rule] as number,
          ],
        ),
      ) as Record<
        TreeGeometryRuntimeFatalRuleKey,
        number
      >;

    return Object.freeze({
      availability:
        'OBSERVED',
      status,
      failureSourceClassificationAvailability:
        'OBSERVED',
      observedAt:
        event.timestamp,

      ...baseCounts,
      ...classificationCounts,

      fatalRuleCounts:
        Object.freeze({
          ...normalizedFatalRuleCounts,
        }),

      readOnly: true,
      repairExecuted: false,
      identitySuppressed: true,
    });
  };


export const useTreeGeometryRuntimeEvidence =
  (): TreeGeometryRuntimeLiveProjection => {
    const revision =
      useSyncExternalStore(
        subscribeGovernorRuntimeEvidence,
        getGovernorRuntimeEvidenceRevision,
        getServerRevision,
      );

    return useMemo(
      () =>
        projectTreeGeometryRuntimeEvidence(
          getGovernorRuntimeEvidenceSnapshot(),
        ),
      [revision],
    );
  };

export const TREE_LEGACY_ANCESTRY_CAUSAL_MAX_AGE_MS =
  10 * 60 * 1000;

export const TREE_LEGACY_ANCESTRY_CAUSAL_COHERENCE_MAX_DELTA_MS =
  5_000;

export type TreeLegacyAncestryCausalAvailability =
  | 'NOT_PUBLISHED'
  | 'OBSERVED'
  | 'INVALID_EVIDENCE';

export interface TreeLegacyAncestryCausalRuleProjection {
  readonly violationCount: number;
  readonly branchDivergentCount: number;
  readonly layoutDivergentCount: number;
  readonly anyDivergentCount: number;
  readonly indeterminateCount: number;
  readonly parityCount: number;
}

export interface TreeLegacyAncestryCausalLiveProjection {
  readonly availability:
    TreeLegacyAncestryCausalAvailability;

  readonly observedAt:
    string | null;

  readonly geometryObservedAt:
    string | null;

  readonly geometryCoherenceDeltaMs:
    number | null;

  readonly branchLegacyFallbackSelected:
    boolean | null;

  readonly layoutLegacyFallbackSelected:
    boolean | null;

  readonly rootBoundaryRejectedCount:
    number | null;

  readonly rootBoundaryMaternalCount:
    number | null;

  readonly rootBoundaryPaternalCount:
    number | null;

  readonly rootBoundaryOtherRootCount:
    number | null;

  readonly rootBoundaryViewerTerminalCandidateCount:
    number | null;

  readonly rootBoundaryOrdinaryCount:
    number | null;

  readonly rootBoundaryCandidateIneligibleCount:
    number | null;

  readonly rootBoundaryEligibleContradictionCount:
    number | null;

  readonly viewerTerminalFailureReasonCounts:
    Readonly<
      Record<
        CanonicalTreeViewerTerminalFailureKey,
        number
      >
    > | null;

  readonly rootBoundaryBranchDivergentCount:
    number | null;

  readonly rootBoundaryLayoutDivergentCount:
    number | null;

  readonly rootBoundaryAnyDivergentCount:
    number | null;

  readonly rootBoundaryIndeterminateCount:
    number | null;

  readonly rootBoundaryParityCount:
    number | null;

  readonly horizontal:
    Readonly<
      Record<
        LegacyAncestryHorizontalRule,
        TreeLegacyAncestryCausalRuleProjection
      >
    > | null;

  readonly horizontalViolationCount:
    number | null;

  readonly horizontalAnyDivergentCount:
    number | null;

  readonly invalidJoinCount:
    number | null;

  readonly coherentGeometryObservation:
    boolean;

  readonly upstreamAuthorityProven:
    boolean | null;

  readonly overlapIsCausation:
    false;

  readonly causalAttributionAllowed:
    false;

  readonly sourceMutationAllowed:
    false;

  readonly readOnly:
    true;

  readonly repairExecuted:
    false;

  readonly identitySuppressed:
    true;
}

const emptyTreeLegacyAncestryCausalEvidence =
  (
    availability:
      TreeLegacyAncestryCausalAvailability =
        'NOT_PUBLISHED',

    observedAt:
      string | null = null,

    geometryObservedAt:
      string | null = null,
  ): TreeLegacyAncestryCausalLiveProjection =>
    Object.freeze({
      availability,
      observedAt,
      geometryObservedAt,
      geometryCoherenceDeltaMs:
        null,

      branchLegacyFallbackSelected:
        null,

      layoutLegacyFallbackSelected:
        null,

      rootBoundaryRejectedCount:
        null,

      rootBoundaryMaternalCount:
        null,

      rootBoundaryPaternalCount:
        null,

      rootBoundaryOtherRootCount:
        null,

      rootBoundaryViewerTerminalCandidateCount:
        null,

      rootBoundaryOrdinaryCount:
        null,

      rootBoundaryCandidateIneligibleCount:
        null,

      rootBoundaryEligibleContradictionCount:
        null,

      viewerTerminalFailureReasonCounts:
        null,

      rootBoundaryBranchDivergentCount:
        null,

      rootBoundaryLayoutDivergentCount:
        null,

      rootBoundaryAnyDivergentCount:
        null,

      rootBoundaryIndeterminateCount:
        null,

      rootBoundaryParityCount:
        null,

      horizontal:
        null,

      horizontalViolationCount:
        null,

      horizontalAnyDivergentCount:
        null,

      invalidJoinCount:
        null,

      coherentGeometryObservation:
        false,

      upstreamAuthorityProven:
        null,

      overlapIsCausation:
        false,

      causalAttributionAllowed:
        false,

      sourceMutationAllowed:
        false,

      readOnly:
        true,

      repairExecuted:
        false,

      identitySuppressed:
        true,
    });

export const projectTreeRuntimeLegacyAncestryCausalEvidence =
  (
    snapshot:
      ReturnType<
        typeof getGovernorRuntimeEvidenceSnapshot
      >,

    nowMs:
      number = Date.now(),
  ): TreeLegacyAncestryCausalLiveProjection => {
    const causalEvent =
      [...snapshot.events]
        .reverse()
        .find(
          candidate =>
            candidate.domain ===
              'tree' &&
            candidate.source ===
              'TreeRuntimeLegacyAncestryResidueCausalLocalization',
        );

    if (!causalEvent) {
      return (
        emptyTreeLegacyAncestryCausalEvidence()
      );
    }

    const evidence =
      causalEvent.evidence;

    const timestampMs =
      Date.parse(
        causalEvent.timestamp,
      );

    const ageMs =
      nowMs -
      timestampMs;

    if (
      !Number.isFinite(
        timestampMs,
      ) ||
      ageMs < 0
    ) {
      return (
        emptyTreeLegacyAncestryCausalEvidence(
          'INVALID_EVIDENCE',
          causalEvent.timestamp,
        )
      );
    }

    if (
      ageMs >
      TREE_LEGACY_ANCESTRY_CAUSAL_MAX_AGE_MS
    ) {
      return (
        emptyTreeLegacyAncestryCausalEvidence()
      );
    }

    const schema =
      evidenceValue(
        evidence,
        'schema',
      );

    const evidenceStatus =
      evidenceValue(
        evidence,
        'evidenceStatus',
      );

    const authenticatedSession =
      evidenceBoolean(
        evidence,
        'authenticatedSession',
      );

    const geometryObservationId =
      evidenceValue(
        evidence,
        'geometryObservationId',
      );

    const branchLegacyFallbackSelected =
      evidenceBoolean(
        evidence,
        'branchLegacyFallbackSelected',
      );

    const layoutLegacyFallbackSelected =
      evidenceBoolean(
        evidence,
        'layoutLegacyFallbackSelected',
      );

    const scalarCountKeys = [
      'rootBoundaryRejectedCount',
      'rootBoundaryMaternalCount',
      'rootBoundaryPaternalCount',
      'rootBoundaryOtherRootCount',
      'rootBoundaryViewerTerminalCandidateCount',
      'rootBoundaryOrdinaryCount',
      'rootBoundaryCandidateIneligibleCount',
      'rootBoundaryEligibleContradictionCount',
      'rootBoundaryBranchDivergentCount',
      'rootBoundaryLayoutDivergentCount',
      'rootBoundaryAnyDivergentCount',
      'rootBoundaryIndeterminateCount',
      'rootBoundaryParityCount',
      'horizontalViolationCount',
      'horizontalAnyDivergentCount',
      'invalidJoinCount',
    ] as const;

    const scalarCounts =
      Object.fromEntries(
        scalarCountKeys.map(
          key => [
            key,
            evidenceCount(
              evidence,
              key,
            ),
          ],
        ),
      ) as Record<
        typeof scalarCountKeys[number],
        number | null
      >;

    const viewerTerminalFailureReasonCounts =
      Object.fromEntries(
        CANONICAL_TREE_VIEWER_TERMINAL_FAILURE_KEYS.map(
          reason => [
            reason,
            evidenceCount(
              evidence,
              `viewerTerminalFailure.${reason}`,
            ),
          ],
        ),
      ) as Record<
        CanonicalTreeViewerTerminalFailureKey,
        number | null
      >;

    const horizontal =
      Object.fromEntries(
        LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
          rule => [
            rule,
            {
              violationCount:
                evidenceCount(
                  evidence,
                  `horizontal.${rule}.violationCount`,
                ),

              branchDivergentCount:
                evidenceCount(
                  evidence,
                  `horizontal.${rule}.branchDivergentCount`,
                ),

              layoutDivergentCount:
                evidenceCount(
                  evidence,
                  `horizontal.${rule}.layoutDivergentCount`,
                ),

              anyDivergentCount:
                evidenceCount(
                  evidence,
                  `horizontal.${rule}.anyDivergentCount`,
                ),

              indeterminateCount:
                evidenceCount(
                  evidence,
                  `horizontal.${rule}.indeterminateCount`,
                ),

              parityCount:
                evidenceCount(
                  evidence,
                  `horizontal.${rule}.parityCount`,
                ),
            },
          ],
        ),
      ) as Record<
        LegacyAncestryHorizontalRule,
        {
          violationCount:
            number | null;
          branchDivergentCount:
            number | null;
          layoutDivergentCount:
            number | null;
          anyDivergentCount:
            number | null;
          indeterminateCount:
            number | null;
          parityCount:
            number | null;
        }
      >;

    const staticTruth = {
      upstreamAuthorityProven:
        evidenceBoolean(
          evidence,
          'upstreamAuthorityProven',
        ),

      overlapIsCausation:
        evidenceBoolean(
          evidence,
          'overlapIsCausation',
        ),

      causalAttributionAllowed:
        evidenceBoolean(
          evidence,
          'causalAttributionAllowed',
        ),

      sourceMutationAllowed:
        evidenceBoolean(
          evidence,
          'sourceMutationAllowed',
        ),

      privateIdentityPublished:
        evidenceBoolean(
          evidence,
          'privateIdentityPublished',
        ),
    };

    const causalEvidenceId =
      evidenceValue(
        evidence,
        'evidenceId',
      );

    const observedAtToken =
      evidenceValue(
        evidence,
        'observedAt',
      );

    const observedAtTokenMs =
      observedAtToken === null
        ? Number.NaN
        : Date.parse(
            observedAtToken,
          );

    const evidenceIdValid =
      causalEvidenceId !== null &&
      /^tree-legacy-ancestry-causal-\d+-\d+$/.test(
        causalEvidenceId,
      );

    const observedAtTokenCoherent =
      Number.isFinite(
        observedAtTokenMs,
      ) &&
      Math.abs(
        timestampMs -
        observedAtTokenMs,
      ) <=
        TREE_LEGACY_ANCESTRY_CAUSAL_COHERENCE_MAX_DELTA_MS;

    const scalarKeys = [
      'schema',
      'authenticatedSession',
      'geometryObservationId',
      'evidenceStatus',
      'branchLegacyFallbackSelected',
      'layoutLegacyFallbackSelected',
      ...scalarCountKeys,
      'upstreamAuthorityProven',
      'overlapIsCausation',
      'causalAttributionAllowed',
      'sourceMutationAllowed',
      'privateIdentityPublished',
      'observedAt',
    ] as const;

    const expectedTokenKeys =
      new Set<string>([
        'evidenceId',
        ...scalarKeys,
        ...CANONICAL_TREE_VIEWER_TERMINAL_FAILURE_KEYS.map(
          reason =>
            `viewerTerminalFailure.${reason}`,
        ),
        ...LEGACY_ANCESTRY_HORIZONTAL_RULES.flatMap(
          rule => [
            `horizontal.${rule}.violationCount`,
            `horizontal.${rule}.branchDivergentCount`,
            `horizontal.${rule}.layoutDivergentCount`,
            `horizontal.${rule}.anyDivergentCount`,
            `horizontal.${rule}.indeterminateCount`,
            `horizontal.${rule}.parityCount`,
          ],
        ),
      ]);

    const exactTokenVocabulary =
      evidence.length ===
        expectedTokenKeys.size &&
      evidence.every(
        token => {
          const separator =
            token.indexOf('=');

          return (
            separator > 0 &&
            expectedTokenKeys.has(
              token.slice(
                0,
                separator,
              ),
            )
          );
        },
      ) &&
      [...expectedTokenKeys].every(
        key =>
          evidenceOccurrenceCount(
            evidence,
            key,
          ) === 1,
      );

    const uniqueScalars =
      scalarKeys.every(
        key =>
          evidenceOccurrenceCount(
            evidence,
            key,
          ) === 1,
      );

    const uniqueReasons =
      CANONICAL_TREE_VIEWER_TERMINAL_FAILURE_KEYS.every(
        reason =>
          evidenceOccurrenceCount(
            evidence,
            `viewerTerminalFailure.${reason}`,
          ) === 1,
      );

    const horizontalFieldNames = [
      'violationCount',
      'branchDivergentCount',
      'layoutDivergentCount',
      'anyDivergentCount',
      'indeterminateCount',
      'parityCount',
    ] as const;

    const uniqueHorizontal =
      LEGACY_ANCESTRY_HORIZONTAL_RULES.every(
        rule =>
          horizontalFieldNames.every(
            field =>
              evidenceOccurrenceCount(
                evidence,
                `horizontal.${rule}.${field}`,
              ) === 1,
          ),
      );

    const forbiddenPrefixes = [
      'memberId=',
      'memberIds=',
      'edgeKey=',
      'edgeKeys=',
      'fromMemberId=',
      'fromMemberIds=',
      'toMemberId=',
      'routePoint=',
      'routePoints=',
      'obstacleMemberId=',
      'obstacleMemberIds=',
      'details=',
      'x=',
      'y=',
    ] as const;

    const privacyValid =
      !evidence.some(
        token =>
          forbiddenPrefixes.some(
            prefix =>
              token.startsWith(
                prefix,
              ),
          ),
      );

    const geometryIdValid =
      geometryObservationId !==
        null &&
      geometryObservationId.startsWith(
        'tree-geometry-observation-',
      ) &&
      geometryObservationId.length <=
        160 &&
      !/\s/.test(
        geometryObservationId,
      );

    const baseValid =
      schema ===
        TREE_RUNTIME_LEGACY_ANCESTRY_CAUSAL_SCHEMA &&
      authenticatedSession ===
        true &&
      evidenceStatus ===
        'OBSERVED' &&
      geometryIdValid &&
      evidenceIdValid &&
      observedAtTokenCoherent &&
      exactTokenVocabulary &&
      causalEvent.repairReady ===
        false &&
      branchLegacyFallbackSelected !==
        null &&
      layoutLegacyFallbackSelected !==
        null &&
      uniqueScalars &&
      uniqueReasons &&
      uniqueHorizontal &&
      privacyValid &&
      Object.values(
        scalarCounts,
      ).every(
        value =>
          value !== null,
      ) &&
      Object.values(
        viewerTerminalFailureReasonCounts,
      ).every(
        value =>
          value !== null,
      ) &&
      Object.values(
        horizontal,
      ).every(
        counts =>
          Object.values(
            counts,
          ).every(
            value =>
              value !== null,
          ),
      ) &&
      staticTruth.upstreamAuthorityProven ===
        true &&
      staticTruth.overlapIsCausation ===
        false &&
      staticTruth.causalAttributionAllowed ===
        false &&
      staticTruth.sourceMutationAllowed ===
        false &&
      staticTruth.privateIdentityPublished ===
        false;

    if (!baseValid) {
      return (
        emptyTreeLegacyAncestryCausalEvidence(
          'INVALID_EVIDENCE',
          causalEvent.timestamp,
        )
      );
    }

    const rejected =
      scalarCounts
        .rootBoundaryRejectedCount as number;

    const sideParity =
      (
        scalarCounts
          .rootBoundaryMaternalCount as number
      ) +
      (
        scalarCounts
          .rootBoundaryPaternalCount as number
      ) +
      (
        scalarCounts
          .rootBoundaryOtherRootCount as number
      ) ===
      rejected;

    const terminalParity =
      (
        scalarCounts
          .rootBoundaryViewerTerminalCandidateCount as number
      ) +
      (
        scalarCounts
          .rootBoundaryOrdinaryCount as number
      ) ===
      rejected;

    const terminalSafety =
      (
        scalarCounts
          .rootBoundaryOtherRootCount === 0
      ) &&
      (
        scalarCounts
          .rootBoundaryEligibleContradictionCount === 0
      ) &&
      (
        scalarCounts
          .rootBoundaryCandidateIneligibleCount ===
        scalarCounts
          .rootBoundaryViewerTerminalCandidateCount
      );

    const rootCohortBoundsValid =
      [
        scalarCounts
          .rootBoundaryBranchDivergentCount,
        scalarCounts
          .rootBoundaryLayoutDivergentCount,
        scalarCounts
          .rootBoundaryAnyDivergentCount,
        scalarCounts
          .rootBoundaryIndeterminateCount,
        scalarCounts
          .rootBoundaryParityCount,
      ].every(
        value =>
          (
            value as number
          ) <= rejected,
      ) &&
      (
        scalarCounts
          .rootBoundaryAnyDivergentCount as number
      ) >=
        Math.max(
          scalarCounts
            .rootBoundaryBranchDivergentCount as number,
          scalarCounts
            .rootBoundaryLayoutDivergentCount as number,
        );

    const reasonBoundsValid =
      Object.values(
        viewerTerminalFailureReasonCounts,
      ).every(
        value =>
          (
            value as number
          ) <= rejected,
      );

    const horizontalValuesValid =
      LEGACY_ANCESTRY_HORIZONTAL_RULES.every(
        rule => {
          const counts =
            horizontal[rule];

          const violation =
            counts.violationCount as number;

          const branch =
            counts.branchDivergentCount as number;

          const layout =
            counts.layoutDivergentCount as number;

          const any =
            counts.anyDivergentCount as number;

          const indeterminate =
            counts.indeterminateCount as number;

          const parity =
            counts.parityCount as number;

          return (
            branch <= violation &&
            layout <= violation &&
            any <= violation &&
            indeterminate <= violation &&
            parity <= violation &&
            any >= branch &&
            any >= layout &&
            (
              parity +
              any +
              indeterminate
            ) >= violation
          );
        },
      );

    const horizontalViolationTotal =
      LEGACY_ANCESTRY_HORIZONTAL_RULES.reduce(
        (total, rule) =>
          total +
          (
            horizontal[rule]
              .violationCount as number
          ),
        0,
      );

    const horizontalAnyTotal =
      LEGACY_ANCESTRY_HORIZONTAL_RULES.reduce(
        (total, rule) =>
          total +
          (
            horizontal[rule]
              .anyDivergentCount as number
          ),
        0,
      );

    const aggregateParity =
      horizontalViolationTotal ===
        scalarCounts
          .horizontalViolationCount &&
      horizontalAnyTotal ===
        scalarCounts
          .horizontalAnyDivergentCount &&
      scalarCounts
        .invalidJoinCount === 0;

    if (
      !sideParity ||
      !terminalParity ||
      !terminalSafety ||
      !rootCohortBoundsValid ||
      !reasonBoundsValid ||
      !horizontalValuesValid ||
      !aggregateParity
    ) {
      return (
        emptyTreeLegacyAncestryCausalEvidence(
          'INVALID_EVIDENCE',
          causalEvent.timestamp,
        )
      );
    }

    /*
     * Exact observation correlation.
     *
     * We do not merely select "the latest geometry".
     * Exactly one geometry publication must carry the
     * same opaque observation ID.
     */
    const matchingGeometryEvents =
      snapshot.events.filter(
        candidate =>
          candidate.domain ===
            'tree' &&
          candidate.source ===
            'TreeGeometryEvidence' &&
          evidenceOccurrenceCount(
            candidate.evidence,
            'geometryObservationId',
          ) === 1 &&
          evidenceValue(
            candidate.evidence,
            'geometryObservationId',
          ) ===
            geometryObservationId,
      );

    if (
      matchingGeometryEvents.length !==
      1
    ) {
      return (
        emptyTreeLegacyAncestryCausalEvidence(
          'INVALID_EVIDENCE',
          causalEvent.timestamp,
        )
      );
    }

    const geometryEvent =
      matchingGeometryEvents[0];

    const geometryProjection =
      projectTreeGeometryRuntimeEvidence(
        {
          ...snapshot,
          events:
            [geometryEvent],
        },
        nowMs,
      );

    const geometryTimestampMs =
      Date.parse(
        geometryEvent.timestamp,
      );

    const coherenceDeltaMs =
      Math.abs(
        timestampMs -
        geometryTimestampMs,
      );

    const coherentTimestamp =
      Number.isFinite(
        geometryTimestampMs,
      ) &&
      coherenceDeltaMs <=
        TREE_LEGACY_ANCESTRY_CAUSAL_COHERENCE_MAX_DELTA_MS;

    const geometryClassificationReady =
      geometryProjection.availability ===
        'OBSERVED' &&
      geometryProjection
        .failureSourceClassificationAvailability ===
        'OBSERVED' &&
      geometryProjection
        .fatalRuleCounts !==
        null;

    const rootGeometryParity =
      geometryProjection
        .routeRejectedRootBoundaryCount ===
        rejected;

    const horizontalGeometryParity =
      geometryProjection
        .fatalRuleCounts !==
        null &&
      LEGACY_ANCESTRY_HORIZONTAL_RULES.every(
        rule =>
          geometryProjection
            .fatalRuleCounts?.[rule] ===
          horizontal[rule]
            .violationCount,
      );

    if (
      !coherentTimestamp ||
      !geometryClassificationReady ||
      !rootGeometryParity ||
      !horizontalGeometryParity
    ) {
      return (
        emptyTreeLegacyAncestryCausalEvidence(
          'INVALID_EVIDENCE',
          causalEvent.timestamp,
          geometryEvent.timestamp,
        )
      );
    }

    const normalizedReasons =
      Object.fromEntries(
        CANONICAL_TREE_VIEWER_TERMINAL_FAILURE_KEYS.map(
          reason => [
            reason,
            viewerTerminalFailureReasonCounts[
              reason
            ] as number,
          ],
        ),
      ) as Record<
        CanonicalTreeViewerTerminalFailureKey,
        number
      >;

    const normalizedHorizontal =
      Object.fromEntries(
        LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
          rule => [
            rule,
            Object.freeze({
              violationCount:
                horizontal[rule]
                  .violationCount as number,

              branchDivergentCount:
                horizontal[rule]
                  .branchDivergentCount as number,

              layoutDivergentCount:
                horizontal[rule]
                  .layoutDivergentCount as number,

              anyDivergentCount:
                horizontal[rule]
                  .anyDivergentCount as number,

              indeterminateCount:
                horizontal[rule]
                  .indeterminateCount as number,

              parityCount:
                horizontal[rule]
                  .parityCount as number,
            }),
          ],
        ),
      ) as Record<
        LegacyAncestryHorizontalRule,
        TreeLegacyAncestryCausalRuleProjection
      >;

    return Object.freeze({
      availability:
        'OBSERVED',

      observedAt:
        causalEvent.timestamp,

      geometryObservedAt:
        geometryEvent.timestamp,

      geometryCoherenceDeltaMs:
        coherenceDeltaMs,

      branchLegacyFallbackSelected:
        branchLegacyFallbackSelected as boolean,

      layoutLegacyFallbackSelected:
        layoutLegacyFallbackSelected as boolean,

      rootBoundaryRejectedCount:
        scalarCounts
          .rootBoundaryRejectedCount as number,

      rootBoundaryMaternalCount:
        scalarCounts
          .rootBoundaryMaternalCount as number,

      rootBoundaryPaternalCount:
        scalarCounts
          .rootBoundaryPaternalCount as number,

      rootBoundaryOtherRootCount:
        scalarCounts
          .rootBoundaryOtherRootCount as number,

      rootBoundaryViewerTerminalCandidateCount:
        scalarCounts
          .rootBoundaryViewerTerminalCandidateCount as number,

      rootBoundaryOrdinaryCount:
        scalarCounts
          .rootBoundaryOrdinaryCount as number,

      rootBoundaryCandidateIneligibleCount:
        scalarCounts
          .rootBoundaryCandidateIneligibleCount as number,

      rootBoundaryEligibleContradictionCount:
        scalarCounts
          .rootBoundaryEligibleContradictionCount as number,

      viewerTerminalFailureReasonCounts:
        Object.freeze(
          normalizedReasons,
        ),

      rootBoundaryBranchDivergentCount:
        scalarCounts
          .rootBoundaryBranchDivergentCount as number,

      rootBoundaryLayoutDivergentCount:
        scalarCounts
          .rootBoundaryLayoutDivergentCount as number,

      rootBoundaryAnyDivergentCount:
        scalarCounts
          .rootBoundaryAnyDivergentCount as number,

      rootBoundaryIndeterminateCount:
        scalarCounts
          .rootBoundaryIndeterminateCount as number,

      rootBoundaryParityCount:
        scalarCounts
          .rootBoundaryParityCount as number,

      horizontal:
        Object.freeze(
          normalizedHorizontal,
        ),

      horizontalViolationCount:
        scalarCounts
          .horizontalViolationCount as number,

      horizontalAnyDivergentCount:
        scalarCounts
          .horizontalAnyDivergentCount as number,

      invalidJoinCount:
        0,

      coherentGeometryObservation:
        true,

      upstreamAuthorityProven:
        true,

      overlapIsCausation:
        false,

      causalAttributionAllowed:
        false,

      sourceMutationAllowed:
        false,

      readOnly:
        true,

      repairExecuted:
        false,

      identitySuppressed:
        true,
    });
  };


export const useTreeRuntimeLegacyAncestryCausalEvidence =
  (): TreeLegacyAncestryCausalLiveProjection => {
    const revision =
      useSyncExternalStore(
        subscribeGovernorRuntimeEvidence,
        getGovernorRuntimeEvidenceRevision,
        getServerRevision,
      );

    return useMemo(
      () =>
        projectTreeRuntimeLegacyAncestryCausalEvidence(
          getGovernorRuntimeEvidenceSnapshot(),
        ),
      [revision],
    );
  };



export type TreeCanonicalBranchCounterfactualAvailability =
  | 'NOT_PUBLISHED'
  | 'OBSERVED'
  | 'INVALID_EVIDENCE';

export interface TreeCanonicalBranchCounterfactualLiveProjection {
  readonly availability:
    TreeCanonicalBranchCounterfactualAvailability;

  readonly observedAt: string | null;
  readonly geometryObservedAt: string | null;
  readonly causalObservedAt: string | null;

  readonly geometryCoherenceDeltaMs: number | null;
  readonly causalCoherenceDeltaMs: number | null;

  readonly coherentGeometryObservation: boolean;
  readonly coherentCausalObservation: boolean;

  readonly controlParity: boolean | null;
  readonly controlWorldParity: boolean | null;
  readonly controlFailureParity: boolean | null;

  readonly controlFatalCount: number | null;
  readonly shadowFatalCount: number | null;
  readonly resolvedFatalCount: number | null;
  readonly persistedFatalCount: number | null;
  readonly introducedFatalCount: number | null;
  readonly indeterminateControlFatalCount: number | null;

  readonly rootBoundary:
    TreeRuntimeCanonicalBranchCounterfactualRuleCounts | null;

  readonly horizontal:
    Readonly<
      Record<
        LegacyAncestryHorizontalRule,
        TreeRuntimeCanonicalBranchCounterfactualRuleCounts
      >
    > | null;

  readonly horizontalControlCount: number | null;
  readonly horizontalResolvedCount: number | null;
  readonly horizontalPersistedCount: number | null;
  readonly horizontalIntroducedCount: number | null;
  readonly horizontalIndeterminateCount: number | null;

  readonly substitutedMemberCount: number | null;
  readonly indeterminateMemberCount: number | null;
  readonly invalidJoinCount: number | null;

  readonly sourceMutationAllowed: false;
  readonly causalAttributionAllowed: false;
  readonly legacyRetirementAuthorized: false;
  readonly productionAuthorityChanged: false;

  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly identitySuppressed: true;
}

const emptyTreeCanonicalBranchCounterfactualEvidence = (
  availability:
    TreeCanonicalBranchCounterfactualAvailability =
      'NOT_PUBLISHED',
  observedAt:
    string | null = null,
): TreeCanonicalBranchCounterfactualLiveProjection =>
  Object.freeze({
    availability,
    observedAt,
    geometryObservedAt:
      null,
    causalObservedAt:
      null,
    geometryCoherenceDeltaMs:
      null,
    causalCoherenceDeltaMs:
      null,
    coherentGeometryObservation:
      false,
    coherentCausalObservation:
      false,
    controlParity:
      null,
    controlWorldParity:
      null,
    controlFailureParity:
      null,
    controlFatalCount:
      null,
    shadowFatalCount:
      null,
    resolvedFatalCount:
      null,
    persistedFatalCount:
      null,
    introducedFatalCount:
      null,
    indeterminateControlFatalCount:
      null,
    rootBoundary:
      null,
    horizontal:
      null,
    horizontalControlCount:
      null,
    horizontalResolvedCount:
      null,
    horizontalPersistedCount:
      null,
    horizontalIntroducedCount:
      null,
    horizontalIndeterminateCount:
      null,
    substitutedMemberCount:
      null,
    indeterminateMemberCount:
      null,
    invalidJoinCount:
      null,
    sourceMutationAllowed:
      false,
    causalAttributionAllowed:
      false,
    legacyRetirementAuthorized:
      false,
    productionAuthorityChanged:
      false,
    readOnly:
      true,
    repairExecuted:
      false,
    identitySuppressed:
      true,
  });

export const projectTreeRuntimeCanonicalBranchCounterfactualEvidence = (
  snapshot:
    ReturnType<
      typeof getGovernorRuntimeEvidenceSnapshot
    >,
  nowMs:
    number = Date.now(),
): TreeCanonicalBranchCounterfactualLiveProjection => {
  const event =
    [...snapshot.events]
      .reverse()
      .find(
        candidate =>
          candidate.domain ===
            'tree' &&
          candidate.source ===
            'TreeRuntimeCanonicalBranchCounterfactualShadowProjection',
      );

  if (!event) {
    return (
      emptyTreeCanonicalBranchCounterfactualEvidence()
    );
  }

  const timestampMs =
    Date.parse(
      event.timestamp,
    );

  const ageMs =
    nowMs -
    timestampMs;

  if (
    !Number.isFinite(
      timestampMs,
    ) ||
    ageMs < 0
  ) {
    return (
      emptyTreeCanonicalBranchCounterfactualEvidence(
        'INVALID_EVIDENCE',
        event.timestamp,
      )
    );
  }

  if (
    ageMs >
    TREE_LEGACY_ANCESTRY_CAUSAL_MAX_AGE_MS
  ) {
    return (
      emptyTreeCanonicalBranchCounterfactualEvidence()
    );
  }

  const evidence =
    event.evidence;

  const schema =
    evidenceValue(
      evidence,
      'schema',
    );

  const authenticatedSession =
    evidenceBoolean(
      evidence,
      'authenticatedSession',
    );

  const geometryObservationId =
    evidenceValue(
      evidence,
      'geometryObservationId',
    );

  const comparisonAvailability =
    evidenceValue(
      evidence,
      'comparisonAvailability',
    );

  const booleanKeys = [
    'controlParity',
    'controlWorldParity',
    'controlFailureParity',
    'sourceMutationAllowed',
    'causalAttributionAllowed',
    'legacyRetirementAuthorized',
    'productionAuthorityChanged',
    'privateIdentityPublished',
  ] as const;

  const booleans =
    Object.fromEntries(
      booleanKeys.map(
        key => [
          key,
          evidenceBoolean(
            evidence,
            key,
          ),
        ],
      ),
    ) as Record<
      typeof booleanKeys[number],
      boolean | null
    >;

  const scalarCountKeys = [
    'controlFatalCount',
    'shadowFatalCount',
    'resolvedFatalCount',
    'persistedFatalCount',
    'introducedFatalCount',
    'indeterminateControlFatalCount',
    'rootBoundaryControlCount',
    'rootBoundaryResolvedCount',
    'rootBoundaryPersistedCount',
    'rootBoundaryIntroducedCount',
    'rootBoundaryIndeterminateCount',
    'horizontalControlCount',
    'horizontalResolvedCount',
    'horizontalPersistedCount',
    'horizontalIntroducedCount',
    'horizontalIndeterminateCount',
    'substitutedMemberCount',
    'indeterminateMemberCount',
    'invalidJoinCount',
  ] as const;

  const counts =
    Object.fromEntries(
      scalarCountKeys.map(
        key => [
          key,
          evidenceCount(
            evidence,
            key,
          ),
        ],
      ),
    ) as Record<
      typeof scalarCountKeys[number],
      number | null
    >;

  const horizontal =
    Object.fromEntries(
      LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
        rule => [
          rule,
          {
            control:
              evidenceCount(
                evidence,
                `horizontal.${rule}.control`,
              ),
            resolved:
              evidenceCount(
                evidence,
                `horizontal.${rule}.resolved`,
              ),
            persisted:
              evidenceCount(
                evidence,
                `horizontal.${rule}.persisted`,
              ),
            introduced:
              evidenceCount(
                evidence,
                `horizontal.${rule}.introduced`,
              ),
            indeterminate:
              evidenceCount(
                evidence,
                `horizontal.${rule}.indeterminate`,
              ),
          },
        ],
      ),
    );

  const evidenceId =
    evidenceValue(
      evidence,
      'evidenceId',
    );

  const observedAtToken =
    evidenceValue(
      evidence,
      'observedAt',
    );

  const observedAtTokenMs =
    observedAtToken === null
      ? Number.NaN
      : Date.parse(
          observedAtToken,
        );

  const geometryIdValid =
    geometryObservationId !==
      null &&
    geometryObservationId
      .startsWith(
        'tree-geometry-observation-',
      ) &&
    geometryObservationId.length <=
      160 &&
    !/\s/.test(
      geometryObservationId,
    );

  const evidenceIdValid =
    evidenceId !== null &&
    /^tree-canonical-branch-counterfactual-\d+-\d+$/.test(
      evidenceId,
    );

  const observedAtValid =
    Number.isFinite(
      observedAtTokenMs,
    ) &&
    Math.abs(
      timestampMs -
      observedAtTokenMs,
    ) <=
      TREE_LEGACY_ANCESTRY_CAUSAL_COHERENCE_MAX_DELTA_MS;

  const horizontalFieldNames = [
    'control',
    'resolved',
    'persisted',
    'introduced',
    'indeterminate',
  ] as const;

  const expectedTokenKeys =
    new Set<string>([
      'evidenceId',
      'schema',
      'authenticatedSession',
      'geometryObservationId',
      'comparisonAvailability',
      ...booleanKeys,
      ...scalarCountKeys,
      'observedAt',
      ...LEGACY_ANCESTRY_HORIZONTAL_RULES.flatMap(
        rule =>
          horizontalFieldNames.map(
            field =>
              `horizontal.${rule}.${field}`,
          ),
      ),
    ]);

  const exactVocabulary =
    evidence.length ===
      expectedTokenKeys.size &&
    evidence.every(
      token => {
        const index =
          token.indexOf('=');

        return (
          index > 0 &&
          expectedTokenKeys.has(
            token.slice(
              0,
              index,
            ),
          )
        );
      },
    ) &&
    [...expectedTokenKeys].every(
      key =>
        evidenceOccurrenceCount(
          evidence,
          key,
        ) === 1,
    );

  const forbiddenPrefixes = [
    'memberId=',
    'memberIds=',
    'edgeKey=',
    'edgeKeys=',
    'fromMemberId=',
    'fromMemberIds=',
    'toMemberId=',
    'routePoint=',
    'routePoints=',
    'obstacleMemberId=',
    'obstacleMemberIds=',
    'details=',
    'x=',
    'y=',
  ] as const;

  const privacyValid =
    !evidence.some(
      token =>
        forbiddenPrefixes.some(
          prefix =>
            token.startsWith(
              prefix,
            ),
        ),
    );

  const baseValid =
    schema ===
      TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA &&
    authenticatedSession ===
      true &&
    comparisonAvailability ===
      'OBSERVED' &&
    geometryIdValid &&
    evidenceIdValid &&
    observedAtValid &&
    exactVocabulary &&
    privacyValid &&
    event.repairReady ===
      false &&
    Object.values(
      counts,
    ).every(
      value =>
        value !== null,
    ) &&
    Object.values(
      booleans,
    ).every(
      value =>
        value !== null,
    ) &&
    booleans.controlParity ===
      true &&
    booleans.controlWorldParity ===
      true &&
    booleans.controlFailureParity ===
      true &&
    booleans.sourceMutationAllowed ===
      false &&
    booleans.causalAttributionAllowed ===
      false &&
    booleans.legacyRetirementAuthorized ===
      false &&
    booleans.productionAuthorityChanged ===
      false &&
    booleans.privateIdentityPublished ===
      false &&
    counts.invalidJoinCount ===
      0;

  const horizontalValuesValid =
    LEGACY_ANCESTRY_HORIZONTAL_RULES.every(
      rule => {
        const value =
          horizontal[rule] as Record<
            typeof horizontalFieldNames[number],
            number | null
          >;

        if (
          !Object.values(
            value,
          ).every(
            count =>
              count !== null,
          )
        ) {
          return false;
        }

        return (
          value.resolved! +
            value.persisted! ===
              value.control! &&
          value.indeterminate! <=
            value.control!
        );
      },
    );

  const arithmeticValid =
    counts.resolvedFatalCount !==
      null &&
    counts.persistedFatalCount !==
      null &&
    counts.controlFatalCount !==
      null &&
    counts.introducedFatalCount !==
      null &&
    counts.shadowFatalCount !==
      null &&
    counts.resolvedFatalCount +
      counts.persistedFatalCount ===
        counts.controlFatalCount &&
    counts.persistedFatalCount +
      counts.introducedFatalCount ===
        counts.shadowFatalCount &&
    counts.rootBoundaryResolvedCount! +
      counts.rootBoundaryPersistedCount! ===
        counts.rootBoundaryControlCount! &&
    counts.rootBoundaryIndeterminateCount! <=
      counts.rootBoundaryControlCount! &&
    horizontalValuesValid;

  const horizontalControlSum =
    LEGACY_ANCESTRY_HORIZONTAL_RULES.reduce(
      (sum, rule) =>
        sum +
        (
          horizontal[rule] as any
        ).control,
      0,
    );

  const horizontalResolvedSum =
    LEGACY_ANCESTRY_HORIZONTAL_RULES.reduce(
      (sum, rule) =>
        sum +
        (
          horizontal[rule] as any
        ).resolved,
      0,
    );

  const horizontalPersistedSum =
    LEGACY_ANCESTRY_HORIZONTAL_RULES.reduce(
      (sum, rule) =>
        sum +
        (
          horizontal[rule] as any
        ).persisted,
      0,
    );

  const horizontalIntroducedSum =
    LEGACY_ANCESTRY_HORIZONTAL_RULES.reduce(
      (sum, rule) =>
        sum +
        (
          horizontal[rule] as any
        ).introduced,
      0,
    );

  const horizontalIndeterminateSum =
    LEGACY_ANCESTRY_HORIZONTAL_RULES.reduce(
      (sum, rule) =>
        sum +
        (
          horizontal[rule] as any
        ).indeterminate,
      0,
    );

  const aggregateValid =
    horizontalControlSum ===
      counts.horizontalControlCount &&
    horizontalResolvedSum ===
      counts.horizontalResolvedCount &&
    horizontalPersistedSum ===
      counts.horizontalPersistedCount &&
    horizontalIntroducedSum ===
      counts.horizontalIntroducedCount &&
    horizontalIndeterminateSum ===
      counts.horizontalIndeterminateCount;

  if (
    !baseValid ||
    !arithmeticValid ||
    !aggregateValid
  ) {
    return (
      emptyTreeCanonicalBranchCounterfactualEvidence(
        'INVALID_EVIDENCE',
        event.timestamp,
      )
    );
  }

  const matchingGeometryEvents =
    snapshot.events.filter(
      candidate =>
        candidate.domain ===
          'tree' &&
        candidate.source ===
          'TreeGeometryEvidence' &&
        evidenceOccurrenceCount(
          candidate.evidence,
          'geometryObservationId',
        ) === 1 &&
        evidenceValue(
          candidate.evidence,
          'geometryObservationId',
        ) ===
          geometryObservationId,
    );

  const matchingCausalEvents =
    snapshot.events.filter(
      candidate =>
        candidate.domain ===
          'tree' &&
        candidate.source ===
          'TreeRuntimeLegacyAncestryResidueCausalLocalization' &&
        evidenceOccurrenceCount(
          candidate.evidence,
          'geometryObservationId',
        ) === 1 &&
        evidenceValue(
          candidate.evidence,
          'geometryObservationId',
        ) ===
          geometryObservationId,
    );

  if (
    matchingGeometryEvents.length !== 1 ||
    matchingCausalEvents.length !== 1
  ) {
    return (
      emptyTreeCanonicalBranchCounterfactualEvidence(
        'INVALID_EVIDENCE',
        event.timestamp,
      )
    );
  }

  const geometryEvent =
    matchingGeometryEvents[0];

  const causalEvent =
    matchingCausalEvents[0];

  const geometryProjection =
    projectTreeGeometryRuntimeEvidence(
      {
        ...snapshot,
        events:
          [geometryEvent],
      },
      nowMs,
    );

  const causalProjection =
    projectTreeRuntimeLegacyAncestryCausalEvidence(
      {
        ...snapshot,
        events:
          [
            geometryEvent,
            causalEvent,
          ],
      },
      nowMs,
    );

  const geometryTimestampMs =
    Date.parse(
      geometryEvent.timestamp,
    );

  const causalTimestampMs =
    Date.parse(
      causalEvent.timestamp,
    );

  const geometryDeltaMs =
    Math.abs(
      timestampMs -
      geometryTimestampMs,
    );

  const causalDeltaMs =
    Math.abs(
      timestampMs -
      causalTimestampMs,
    );

  const exactControlParity =
    geometryProjection.availability ===
      'OBSERVED' &&
    geometryProjection
      .failureSourceClassificationAvailability ===
      'OBSERVED' &&
    geometryProjection
      .fatalRuleCounts !== null &&
    causalProjection.availability ===
      'OBSERVED' &&
    causalProjection
      .coherentGeometryObservation &&
    geometryProjection.fatalIssueCount ===
      counts.controlFatalCount &&
    geometryProjection
      .routeRejectedRootBoundaryCount ===
      counts.rootBoundaryControlCount &&
    causalProjection
      .rootBoundaryRejectedCount ===
      counts.rootBoundaryControlCount &&
    causalProjection.horizontal !==
      null &&
    LEGACY_ANCESTRY_HORIZONTAL_RULES.every(
      rule =>
        geometryProjection
          .fatalRuleCounts?.[rule] ===
          (
            horizontal[rule] as any
          ).control &&
        causalProjection
          .horizontal?.[rule]
          .violationCount ===
          (
            horizontal[rule] as any
          ).control,
    );

  const coherent =
    Number.isFinite(
      geometryTimestampMs,
    ) &&
    Number.isFinite(
      causalTimestampMs,
    ) &&
    geometryDeltaMs <=
      TREE_LEGACY_ANCESTRY_CAUSAL_COHERENCE_MAX_DELTA_MS &&
    causalDeltaMs <=
      TREE_LEGACY_ANCESTRY_CAUSAL_COHERENCE_MAX_DELTA_MS;

  if (
    !exactControlParity ||
    !coherent
  ) {
    return (
      emptyTreeCanonicalBranchCounterfactualEvidence(
        'INVALID_EVIDENCE',
        event.timestamp,
      )
    );
  }

  const normalizedHorizontal =
    Object.fromEntries(
      LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
        rule => [
          rule,
          Object.freeze({
            control:
              (horizontal[rule] as any).control as number,
            resolved:
              (horizontal[rule] as any).resolved as number,
            persisted:
              (horizontal[rule] as any).persisted as number,
            introduced:
              (horizontal[rule] as any).introduced as number,
            indeterminate:
              (horizontal[rule] as any).indeterminate as number,
          }),
        ],
      ),
    ) as Record<
      LegacyAncestryHorizontalRule,
      TreeRuntimeCanonicalBranchCounterfactualRuleCounts
    >;

  return Object.freeze({
    availability:
      'OBSERVED',

    observedAt:
      event.timestamp,

    geometryObservedAt:
      geometryEvent.timestamp,

    causalObservedAt:
      causalEvent.timestamp,

    geometryCoherenceDeltaMs:
      geometryDeltaMs,

    causalCoherenceDeltaMs:
      causalDeltaMs,

    coherentGeometryObservation:
      true,

    coherentCausalObservation:
      true,

    controlParity:
      true,

    controlWorldParity:
      true,

    controlFailureParity:
      true,

    controlFatalCount:
      counts.controlFatalCount as number,

    shadowFatalCount:
      counts.shadowFatalCount as number,

    resolvedFatalCount:
      counts.resolvedFatalCount as number,

    persistedFatalCount:
      counts.persistedFatalCount as number,

    introducedFatalCount:
      counts.introducedFatalCount as number,

    indeterminateControlFatalCount:
      counts.indeterminateControlFatalCount as number,

    rootBoundary:
      Object.freeze({
        control:
          counts.rootBoundaryControlCount as number,
        resolved:
          counts.rootBoundaryResolvedCount as number,
        persisted:
          counts.rootBoundaryPersistedCount as number,
        introduced:
          counts.rootBoundaryIntroducedCount as number,
        indeterminate:
          counts.rootBoundaryIndeterminateCount as number,
      }),

    horizontal:
      Object.freeze({
        ...normalizedHorizontal,
      }),

    horizontalControlCount:
      counts.horizontalControlCount as number,

    horizontalResolvedCount:
      counts.horizontalResolvedCount as number,

    horizontalPersistedCount:
      counts.horizontalPersistedCount as number,

    horizontalIntroducedCount:
      counts.horizontalIntroducedCount as number,

    horizontalIndeterminateCount:
      counts.horizontalIndeterminateCount as number,

    substitutedMemberCount:
      counts.substitutedMemberCount as number,

    indeterminateMemberCount:
      counts.indeterminateMemberCount as number,

    invalidJoinCount:
      0,

    sourceMutationAllowed:
      false,

    causalAttributionAllowed:
      false,

    legacyRetirementAuthorized:
      false,

    productionAuthorityChanged:
      false,

    readOnly:
      true,

    repairExecuted:
      false,

    identitySuppressed:
      true,
  });
};

export const useTreeRuntimeCanonicalBranchCounterfactualEvidence =
  (): TreeCanonicalBranchCounterfactualLiveProjection => {
    const revision =
      useSyncExternalStore(
        subscribeGovernorRuntimeEvidence,
        getGovernorRuntimeEvidenceRevision,
        getServerRevision,
      );

    return useMemo(
      () =>
        projectTreeRuntimeCanonicalBranchCounterfactualEvidence(
          getGovernorRuntimeEvidenceSnapshot(),
        ),
      [revision],
    );
  };



export type TreeBranchLayoutInteractionAvailability =
  | 'NOT_PUBLISHED'
  | 'OBSERVED'
  | 'INVALID_EVIDENCE';

export interface TreeBranchLayoutInteractionCellProjection {
  readonly availability:
    'OBSERVED' | null;

  readonly controlFatalCount:
    number | null;

  readonly shadowFatalCount:
    number | null;

  readonly resolvedFatalCount:
    number | null;

  readonly persistedFatalCount:
    number | null;

  readonly introducedFatalCount:
    number | null;

  readonly indeterminateControlFatalCount:
    number | null;

  readonly rootBoundary:
    TreeRuntimeCanonicalBranchCounterfactualRuleCounts | null;

  readonly horizontal:
    Readonly<
      Record<
        LegacyAncestryHorizontalRule,
        TreeRuntimeCanonicalBranchCounterfactualRuleCounts
      >
    > | null;
}

export interface TreeBranchLayoutInteractionLiveProjection {
  readonly availability:
    TreeBranchLayoutInteractionAvailability;

  readonly observedAt:
    string | null;

  readonly geometryObservedAt:
    string | null;

  readonly branchCounterfactualObservedAt:
    string | null;

  readonly geometryCoherenceDeltaMs:
    number | null;

  readonly branchCounterfactualCoherenceDeltaMs:
    number | null;

  readonly coherentGeometryObservation:
    boolean;

  readonly coherentBranchCounterfactualObservation:
    boolean;

  readonly controlParity:
    boolean | null;

  readonly controlWorldParity:
    boolean | null;

  readonly controlFailureParity:
    boolean | null;

  readonly cellBReferenceCoherent:
    boolean | null;

  readonly cellA:
    Readonly<{
      fatalCount: number;
      rootBoundaryCount: number;
      horizontalCount: number;
      horizontal:
        Readonly<
          Record<
            LegacyAncestryHorizontalRule,
            number
          >
        >;
    }> | null;

  readonly cellB:
    TreeBranchLayoutInteractionCellProjection | null;

  readonly cellC:
    TreeBranchLayoutInteractionCellProjection | null;

  readonly cellD:
    TreeBranchLayoutInteractionCellProjection | null;

  readonly semanticSubstitutedMemberCount:
    number | null;

  readonly semanticIndeterminateMemberCount:
    number | null;

  readonly layoutSubstitutedMemberCount:
    number | null;

  readonly layoutIndeterminateMemberCount:
    number | null;

  readonly sourceMutationAllowed:
    false;

  readonly causalAttributionAllowed:
    false;

  readonly repairAuthorized:
    false;

  readonly legacyRetirementAuthorized:
    false;

  readonly productionAuthorityChanged:
    false;

  readonly readOnly:
    true;

  readonly repairExecuted:
    false;

  readonly identitySuppressed:
    true;
}

const emptyTreeBranchLayoutInteractionEvidence = (
  availability:
    TreeBranchLayoutInteractionAvailability =
      'NOT_PUBLISHED',

  observedAt:
    string | null = null,
): TreeBranchLayoutInteractionLiveProjection =>
  Object.freeze({
    availability,

    observedAt,

    geometryObservedAt:
      null,

    branchCounterfactualObservedAt:
      null,

    geometryCoherenceDeltaMs:
      null,

    branchCounterfactualCoherenceDeltaMs:
      null,

    coherentGeometryObservation:
      false,

    coherentBranchCounterfactualObservation:
      false,

    controlParity:
      null,

    controlWorldParity:
      null,

    controlFailureParity:
      null,

    cellBReferenceCoherent:
      null,

    cellA:
      null,

    cellB:
      null,

    cellC:
      null,

    cellD:
      null,

    semanticSubstitutedMemberCount:
      null,

    semanticIndeterminateMemberCount:
      null,

    layoutSubstitutedMemberCount:
      null,

    layoutIndeterminateMemberCount:
      null,

    sourceMutationAllowed:
      false,

    causalAttributionAllowed:
      false,

    repairAuthorized:
      false,

    legacyRetirementAuthorized:
      false,

    productionAuthorityChanged:
      false,

    readOnly:
      true,

    repairExecuted:
      false,

    identitySuppressed:
      true,
  });

export const projectTreeRuntimeBranchLayoutInteractionEvidence = (
  snapshot:
    ReturnType<
      typeof getGovernorRuntimeEvidenceSnapshot
    >,

  nowMs:
    number = Date.now(),
): TreeBranchLayoutInteractionLiveProjection => {
  const event =
    [...snapshot.events]
      .reverse()
      .find(
        candidate =>
          candidate.domain ===
            'tree' &&
          candidate.source ===
            'TreeRuntimeBranchLayoutInteractionCounterfactualComparison',
      );

  if (!event) {
    return (
      emptyTreeBranchLayoutInteractionEvidence()
    );
  }

  const timestampMs =
    Date.parse(
      event.timestamp,
    );

  const ageMs =
    nowMs -
    timestampMs;

  if (
    !Number.isFinite(
      timestampMs,
    ) ||
    ageMs < 0
  ) {
    return (
      emptyTreeBranchLayoutInteractionEvidence(
        'INVALID_EVIDENCE',
        event.timestamp,
      )
    );
  }

  if (
    ageMs >
    TREE_LEGACY_ANCESTRY_CAUSAL_MAX_AGE_MS
  ) {
    return (
      emptyTreeBranchLayoutInteractionEvidence()
    );
  }

  const evidence =
    event.evidence;

  const schema =
    evidenceValue(
      evidence,
      'schema',
    );

  const authenticatedSession =
    evidenceBoolean(
      evidence,
      'authenticatedSession',
    );

  const geometryObservationId =
    evidenceValue(
      evidence,
      'geometryObservationId',
    );

  const comparisonAvailability =
    evidenceValue(
      evidence,
      'comparisonAvailability',
    );

  const cellBAvailability =
    evidenceValue(
      evidence,
      'cellB.availability',
    );

  const cellCAvailability =
    evidenceValue(
      evidence,
      'cellC.availability',
    );

  const cellDAvailability =
    evidenceValue(
      evidence,
      'cellD.availability',
    );

  const booleanKeys = [
    'controlParity',
    'controlWorldParity',
    'controlFailureParity',
    'cellBReferenceCoherent',
    'sourceMutationAllowed',
    'causalAttributionAllowed',
    'repairAuthorized',
    'legacyRetirementAuthorized',
    'productionAuthorityChanged',
    'privateIdentityPublished',
    'repairReady',
    'readOnly',
    'repairExecuted',
  ] as const;

  const booleans =
    Object.fromEntries(
      booleanKeys.map(
        key => [
          key,
          evidenceBoolean(
            evidence,
            key,
          ),
        ],
      ),
    ) as Record<
      typeof booleanKeys[number],
      boolean | null
    >;

  const count = (
    key: string,
  ): number | null =>
    evidenceCount(
      evidence,
      key,
    );

  const rule = (
    prefix: string,
  ) => ({
    control:
      count(
        prefix +
        '.control',
      ),

    resolved:
      count(
        prefix +
        '.resolved',
      ),

    persisted:
      count(
        prefix +
        '.persisted',
      ),

    introduced:
      count(
        prefix +
        '.introduced',
      ),

    indeterminate:
      count(
        prefix +
        '.indeterminate',
      ),
  });

  const horizontal = (
    prefix: string,
  ) =>
    Object.fromEntries(
      LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
        name => [
          name,
          rule(
            prefix +
            '.' +
            name,
          ),
        ],
      ),
    ) as Record<
      LegacyAncestryHorizontalRule,
      {
        control: number | null;
        resolved: number | null;
        persisted: number | null;
        introduced: number | null;
        indeterminate: number | null;
      }
    >;

  const cellAHorizontal =
    Object.fromEntries(
      LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
        name => [
          name,
          count(
            'cellA.horizontal.' +
            name,
          ),
        ],
      ),
    ) as Record<
      LegacyAncestryHorizontalRule,
      number | null
    >;

  const cellARaw = {
    fatalCount:
      count(
        'cellA.fatalCount',
      ),

    rootBoundaryCount:
      count(
        'cellA.rootBoundaryCount',
      ),

    horizontalCount:
      count(
        'cellA.horizontalCount',
      ),

    horizontal:
      cellAHorizontal,
  };

  const cellBRaw = {
    availability:
      cellBAvailability,

    controlFatalCount:
      count(
        'cellB.controlFatalCount',
      ),

    shadowFatalCount:
      count(
        'cellB.shadowFatalCount',
      ),

    resolvedFatalCount:
      count(
        'cellB.resolvedFatalCount',
      ),

    persistedFatalCount:
      count(
        'cellB.persistedFatalCount',
      ),

    introducedFatalCount:
      count(
        'cellB.introducedFatalCount',
      ),

    indeterminateControlFatalCount:
      count(
        'cellB.indeterminateControlFatalCount',
      ),

    rootBoundary:
      rule(
        'cellB.rootBoundary',
      ),

    horizontal:
      horizontal(
        'cellB.horizontal',
      ),
  };

  const shadowCell = (
    prefix: 'cellC' | 'cellD',
    availability: string | null,
  ) => ({
    availability,

    shadowFatalCount:
      count(
        prefix +
        '.shadowFatalCount',
      ),

    resolvedFatalCount:
      count(
        prefix +
        '.resolvedFatalCount',
      ),

    persistedFatalCount:
      count(
        prefix +
        '.persistedFatalCount',
      ),

    introducedFatalCount:
      count(
        prefix +
        '.introducedFatalCount',
      ),

    indeterminateControlFatalCount:
      count(
        prefix +
        '.indeterminateControlFatalCount',
      ),

    rootBoundary:
      rule(
        prefix +
        '.rootBoundary',
      ),

    horizontal:
      horizontal(
        prefix +
        '.horizontal',
      ),
  });

  const cellCRaw =
    shadowCell(
      'cellC',
      cellCAvailability,
    );

  const cellDRaw =
    shadowCell(
      'cellD',
      cellDAvailability,
    );

  const semanticSubstitutedMemberCount =
    count(
      'semanticSubstitutedMemberCount',
    );

  const semanticIndeterminateMemberCount =
    count(
      'semanticIndeterminateMemberCount',
    );

  const layoutSubstitutedMemberCount =
    count(
      'layoutSubstitutedMemberCount',
    );

  const layoutIndeterminateMemberCount =
    count(
      'layoutIndeterminateMemberCount',
    );

  const evidenceId =
    evidenceValue(
      evidence,
      'evidenceId',
    );

  const observedAtToken =
    evidenceValue(
      evidence,
      'observedAt',
    );

  const observedAtTokenMs =
    observedAtToken ===
      null
      ? Number.NaN
      : Date.parse(
          observedAtToken,
        );

  const geometryIdValid =
    geometryObservationId !==
      null &&
    geometryObservationId
      .startsWith(
        'tree-geometry-observation-',
      ) &&
    geometryObservationId.length <=
      160 &&
    !/\s/.test(
      geometryObservationId,
    );

  const evidenceIdValid =
    evidenceId !==
      null &&
    /^tree-branch-layout-interaction-\d+-\d+$/.test(
      evidenceId,
    );

  const observedAtValid =
    Number.isFinite(
      observedAtTokenMs,
    ) &&
    Math.abs(
      timestampMs -
      observedAtTokenMs,
    ) <=
      TREE_LEGACY_ANCESTRY_CAUSAL_COHERENCE_MAX_DELTA_MS;

  const ruleFields = [
    'control',
    'resolved',
    'persisted',
    'introduced',
    'indeterminate',
  ] as const;

  const expectedTokenKeys =
    new Set<string>([
      'evidenceId',
      'schema',
      'authenticatedSession',
      'geometryObservationId',
      'comparisonAvailability',
      ...booleanKeys,

      'cellA.fatalCount',
      'cellA.rootBoundaryCount',
      'cellA.horizontalCount',

      ...LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
        name =>
          'cellA.horizontal.' +
          name,
      ),

      'cellB.availability',
      'cellB.controlFatalCount',
      'cellB.shadowFatalCount',
      'cellB.resolvedFatalCount',
      'cellB.persistedFatalCount',
      'cellB.introducedFatalCount',
      'cellB.indeterminateControlFatalCount',

      ...ruleFields.map(
        field =>
          'cellB.rootBoundary.' +
          field,
      ),

      ...LEGACY_ANCESTRY_HORIZONTAL_RULES.flatMap(
        name =>
          ruleFields.map(
            field =>
              'cellB.horizontal.' +
              name +
              '.' +
              field,
          ),
      ),

      'cellC.availability',
      'cellC.shadowFatalCount',
      'cellC.resolvedFatalCount',
      'cellC.persistedFatalCount',
      'cellC.introducedFatalCount',
      'cellC.indeterminateControlFatalCount',

      ...ruleFields.map(
        field =>
          'cellC.rootBoundary.' +
          field,
      ),

      ...LEGACY_ANCESTRY_HORIZONTAL_RULES.flatMap(
        name =>
          ruleFields.map(
            field =>
              'cellC.horizontal.' +
              name +
              '.' +
              field,
          ),
      ),

      'cellD.availability',
      'cellD.shadowFatalCount',
      'cellD.resolvedFatalCount',
      'cellD.persistedFatalCount',
      'cellD.introducedFatalCount',
      'cellD.indeterminateControlFatalCount',

      ...ruleFields.map(
        field =>
          'cellD.rootBoundary.' +
          field,
      ),

      ...LEGACY_ANCESTRY_HORIZONTAL_RULES.flatMap(
        name =>
          ruleFields.map(
            field =>
              'cellD.horizontal.' +
              name +
              '.' +
              field,
          ),
      ),

      'semanticSubstitutedMemberCount',
      'semanticIndeterminateMemberCount',
      'layoutSubstitutedMemberCount',
      'layoutIndeterminateMemberCount',

      'observedAt',
    ]);

  const exactVocabulary =
    evidence.length ===
      expectedTokenKeys.size &&

    evidence.every(
      token => {
        const index =
          token.indexOf('=');

        return (
          index >
            0 &&
          expectedTokenKeys.has(
            token.slice(
              0,
              index,
            ),
          )
        );
      },
    ) &&

    [...expectedTokenKeys].every(
      key =>
        evidenceOccurrenceCount(
          evidence,
          key,
        ) ===
          1,
    );

  const privacyValid =
    !evidence.some(
      token =>
        /memberIds?=|edgeKeys?=|routePoints?=|coordinates=|privateIssueKey=|obstacleMemberIds?=|fromMemberIds?=|toMemberId=|details=|^x=|^y=/.test(
          token,
        ),
    );

  const scalarValuesValid =
    [
      cellARaw.fatalCount,
      cellARaw.rootBoundaryCount,
      cellARaw.horizontalCount,

      ...Object.values(
        cellAHorizontal,
      ),

      cellBRaw.controlFatalCount,
      cellBRaw.shadowFatalCount,
      cellBRaw.resolvedFatalCount,
      cellBRaw.persistedFatalCount,
      cellBRaw.introducedFatalCount,
      cellBRaw.indeterminateControlFatalCount,

      cellCRaw.shadowFatalCount,
      cellCRaw.resolvedFatalCount,
      cellCRaw.persistedFatalCount,
      cellCRaw.introducedFatalCount,
      cellCRaw.indeterminateControlFatalCount,

      cellDRaw.shadowFatalCount,
      cellDRaw.resolvedFatalCount,
      cellDRaw.persistedFatalCount,
      cellDRaw.introducedFatalCount,
      cellDRaw.indeterminateControlFatalCount,

      semanticSubstitutedMemberCount,
      semanticIndeterminateMemberCount,
      layoutSubstitutedMemberCount,
      layoutIndeterminateMemberCount,
    ].every(
      value =>
        value !==
          null,
    );

  const ruleValid = (
    value: {
      control: number | null;
      resolved: number | null;
      persisted: number | null;
      introduced: number | null;
      indeterminate: number | null;
    },
  ): boolean =>
    Object.values(
      value,
    ).every(
      countValue =>
        countValue !==
          null,
    ) &&

    (
      value.resolved as number
    ) +
      (
        value.persisted as number
      ) ===
        value.control &&

    (
      value.indeterminate as number
    ) <=
      (
        value.control as number
      );

  const horizontalValid = (
    value:
      Record<
        LegacyAncestryHorizontalRule,
        {
          control: number | null;
          resolved: number | null;
          persisted: number | null;
          introduced: number | null;
          indeterminate: number | null;
        }
      >,
  ): boolean =>
    LEGACY_ANCESTRY_HORIZONTAL_RULES.every(
      name =>
        ruleValid(
          value[name],
        ),
    );

  const aHorizontalSum =
    Object.values(
      cellAHorizontal,
    ).reduce(
      (
        total,
        value,
      ) =>
        total +
        (
          value as number
        ),
      0,
    );

  const sameControlCohort = (
    root: {
      control: number | null;
    },

    horizontalValue:
      Record<
        LegacyAncestryHorizontalRule,
        {
          control: number | null;
        }
      >,
  ): boolean =>
    root.control ===
      cellARaw.rootBoundaryCount &&

    LEGACY_ANCESTRY_HORIZONTAL_RULES.every(
      name =>
        horizontalValue[
          name
        ].control ===
          cellAHorizontal[
            name
          ],
    );

  const bArithmetic =
    scalarValuesValid &&
    ruleValid(
      cellBRaw.rootBoundary,
    ) &&
    horizontalValid(
      cellBRaw.horizontal,
    ) &&
    cellBRaw.resolvedFatalCount! +
      cellBRaw.persistedFatalCount! ===
        cellBRaw.controlFatalCount &&
    cellBRaw.persistedFatalCount! +
      cellBRaw.introducedFatalCount! ===
        cellBRaw.shadowFatalCount &&
    cellBRaw.indeterminateControlFatalCount! <=
      cellBRaw.controlFatalCount! &&
    cellBRaw.controlFatalCount ===
      cellARaw.fatalCount &&
    sameControlCohort(
      cellBRaw.rootBoundary,
      cellBRaw.horizontal,
    );

  const shadowArithmetic = (
    value:
      typeof cellCRaw,
  ): boolean =>
    ruleValid(
      value.rootBoundary,
    ) &&
    horizontalValid(
      value.horizontal,
    ) &&
    value.resolvedFatalCount! +
      value.persistedFatalCount! ===
        cellARaw.fatalCount &&
    value.persistedFatalCount! +
      value.introducedFatalCount! ===
        value.shadowFatalCount &&
    value.indeterminateControlFatalCount! <=
      cellARaw.fatalCount! &&
    sameControlCohort(
      value.rootBoundary,
      value.horizontal,
    );

  const cellAValid =
    cellARaw.fatalCount !==
      null &&
    cellARaw.rootBoundaryCount !==
      null &&
    cellARaw.horizontalCount !==
      null &&
    Object.values(
      cellAHorizontal,
    ).every(
      value =>
        value !==
          null,
    ) &&
    aHorizontalSum ===
      cellARaw.horizontalCount;

  const baseValid =
    schema ===
      TREE_RUNTIME_BRANCH_LAYOUT_INTERACTION_COMPARISON_SCHEMA &&

    authenticatedSession ===
      true &&

    comparisonAvailability ===
      'OBSERVED' &&

    cellBAvailability ===
      'OBSERVED' &&

    cellCAvailability ===
      'OBSERVED' &&

    cellDAvailability ===
      'OBSERVED' &&

    geometryIdValid &&

    evidenceIdValid &&

    observedAtValid &&

    exactVocabulary &&

    privacyValid &&

    event.repairReady ===
      false &&

    Object.values(
      booleans,
    ).every(
      value =>
        value !==
          null,
    ) &&

    booleans.controlParity ===
      true &&

    booleans.controlWorldParity ===
      true &&

    booleans.controlFailureParity ===
      true &&

    booleans.cellBReferenceCoherent ===
      true &&

    booleans.sourceMutationAllowed ===
      false &&

    booleans.causalAttributionAllowed ===
      false &&

    booleans.repairAuthorized ===
      false &&

    booleans.legacyRetirementAuthorized ===
      false &&

    booleans.productionAuthorityChanged ===
      false &&

    booleans.privateIdentityPublished ===
      false &&

    booleans.repairReady ===
      false &&

    booleans.readOnly ===
      true &&

    booleans.repairExecuted ===
      false &&

    semanticSubstitutedMemberCount !==
      null &&

    semanticSubstitutedMemberCount >
      0 &&

    layoutSubstitutedMemberCount !==
      null &&

    layoutSubstitutedMemberCount >
      0;

  if (
    !baseValid ||
    !cellAValid ||
    !bArithmetic ||
    !shadowArithmetic(
      cellCRaw,
    ) ||
    !shadowArithmetic(
      cellDRaw,
    )
  ) {
    return (
      emptyTreeBranchLayoutInteractionEvidence(
        'INVALID_EVIDENCE',
        event.timestamp,
      )
    );
  }

  const matchingGeometryEvents =
    snapshot.events.filter(
      candidate =>
        candidate.domain ===
          'tree' &&
        candidate.source ===
          'TreeGeometryEvidence' &&
        evidenceOccurrenceCount(
          candidate.evidence,
          'geometryObservationId',
        ) ===
          1 &&
        evidenceValue(
          candidate.evidence,
          'geometryObservationId',
        ) ===
          geometryObservationId,
    );

  const matchingBranchEvents =
    snapshot.events.filter(
      candidate =>
        candidate.domain ===
          'tree' &&
        candidate.source ===
          'TreeRuntimeCanonicalBranchCounterfactualShadowProjection' &&
        evidenceOccurrenceCount(
          candidate.evidence,
          'geometryObservationId',
        ) ===
          1 &&
        evidenceValue(
          candidate.evidence,
          'geometryObservationId',
        ) ===
          geometryObservationId,
    );

  if (
    matchingGeometryEvents.length !==
      1 ||
    matchingBranchEvents.length !==
      1
  ) {
    return (
      emptyTreeBranchLayoutInteractionEvidence(
        'INVALID_EVIDENCE',
        event.timestamp,
      )
    );
  }

  const geometryEvent =
    matchingGeometryEvents[0];

  const branchEvent =
    matchingBranchEvents[0];

  const matchingCausalEvents =
    snapshot.events.filter(
      candidate =>
        candidate.domain ===
          'tree' &&
        candidate.source ===
          'TreeRuntimeLegacyAncestryResidueCausalLocalization' &&
        evidenceOccurrenceCount(
          candidate.evidence,
          'geometryObservationId',
        ) ===
          1 &&
        evidenceValue(
          candidate.evidence,
          'geometryObservationId',
        ) ===
          geometryObservationId,
    );

  const geometryProjection =
    projectTreeGeometryRuntimeEvidence(
      {
        ...snapshot,
        events:
          [geometryEvent],
      },
      nowMs,
    );

  const branchProjection =
    projectTreeRuntimeCanonicalBranchCounterfactualEvidence(
      {
        ...snapshot,
        events: [
          geometryEvent,
          ...matchingCausalEvents,
          branchEvent,
        ],
      },
      nowMs,
    );

  const geometryTimestampMs =
    Date.parse(
      geometryEvent.timestamp,
    );

  const branchTimestampMs =
    Date.parse(
      branchEvent.timestamp,
    );

  const geometryDeltaMs =
    Math.abs(
      timestampMs -
      geometryTimestampMs,
    );

  const branchDeltaMs =
    Math.abs(
      timestampMs -
      branchTimestampMs,
    );

  const coherent =
    Number.isFinite(
      geometryTimestampMs,
    ) &&
    Number.isFinite(
      branchTimestampMs,
    ) &&
    geometryDeltaMs <=
      TREE_LEGACY_ANCESTRY_CAUSAL_COHERENCE_MAX_DELTA_MS &&
    branchDeltaMs <=
      TREE_LEGACY_ANCESTRY_CAUSAL_COHERENCE_MAX_DELTA_MS;

  const geometryParity =
    geometryProjection.availability ===
      'OBSERVED' &&
    geometryProjection
      .failureSourceClassificationAvailability ===
      'OBSERVED' &&
    geometryProjection
      .fatalRuleCounts !==
        null &&
    geometryProjection
      .fatalIssueCount ===
        cellARaw.fatalCount &&
    geometryProjection
      .routeRejectedRootBoundaryCount ===
        cellARaw.rootBoundaryCount &&
    LEGACY_ANCESTRY_HORIZONTAL_RULES.every(
      name =>
        geometryProjection
          .fatalRuleCounts?.[
            name
          ] ===
            cellAHorizontal[
              name
            ],
    );

  const branchParity =
    branchProjection.availability ===
      'OBSERVED' &&

    branchProjection
      .coherentGeometryObservation ===
        true &&

    branchProjection
      .coherentCausalObservation ===
        true &&

    branchProjection.controlFatalCount ===
      cellBRaw.controlFatalCount &&

    branchProjection.shadowFatalCount ===
      cellBRaw.shadowFatalCount &&

    branchProjection.resolvedFatalCount ===
      cellBRaw.resolvedFatalCount &&

    branchProjection.persistedFatalCount ===
      cellBRaw.persistedFatalCount &&

    branchProjection.introducedFatalCount ===
      cellBRaw.introducedFatalCount &&

    branchProjection.indeterminateControlFatalCount ===
      cellBRaw.indeterminateControlFatalCount &&

    branchProjection.rootBoundary !==
      null &&

    ruleFields.every(
      field =>
        branchProjection
          .rootBoundary?.[
            field
          ] ===
            cellBRaw
              .rootBoundary[
                field
              ],
    ) &&

    branchProjection.horizontal !==
      null &&

    LEGACY_ANCESTRY_HORIZONTAL_RULES.every(
      name =>
        ruleFields.every(
          field =>
            branchProjection
              .horizontal?.[
                name
              ][
                field
              ] ===
                cellBRaw
                  .horizontal[
                    name
                  ][
                    field
                  ],
        ),
    ) &&

    branchProjection.substitutedMemberCount ===
      semanticSubstitutedMemberCount &&

    branchProjection.indeterminateMemberCount ===
      semanticIndeterminateMemberCount &&

    branchProjection.invalidJoinCount ===
      0;

  if (
    !coherent ||
    !geometryParity ||
    !branchParity
  ) {
    return (
      emptyTreeBranchLayoutInteractionEvidence(
        'INVALID_EVIDENCE',
        event.timestamp,
      )
    );
  }

  const normalizeRule = (
    value: {
      control: number | null;
      resolved: number | null;
      persisted: number | null;
      introduced: number | null;
      indeterminate: number | null;
    },
  ): TreeRuntimeCanonicalBranchCounterfactualRuleCounts =>
    Object.freeze({
      control:
        value.control as number,

      resolved:
        value.resolved as number,

      persisted:
        value.persisted as number,

      introduced:
        value.introduced as number,

      indeterminate:
        value.indeterminate as number,
    });

  const normalizeHorizontal = (
    value:
      Record<
        LegacyAncestryHorizontalRule,
        {
          control: number | null;
          resolved: number | null;
          persisted: number | null;
          introduced: number | null;
          indeterminate: number | null;
        }
      >,
  ) =>
    Object.freeze(
      Object.fromEntries(
        LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
          name => [
            name,
            normalizeRule(
              value[
                name
              ],
            ),
          ],
        ),
      ),
    ) as Readonly<
      Record<
        LegacyAncestryHorizontalRule,
        TreeRuntimeCanonicalBranchCounterfactualRuleCounts
      >
    >;

  const normalizeCell = (
    value:
      typeof cellCRaw,

    controlFatalCount:
      number,
  ): TreeBranchLayoutInteractionCellProjection =>
    Object.freeze({
      availability:
        'OBSERVED',

      controlFatalCount,

      shadowFatalCount:
        value.shadowFatalCount as number,

      resolvedFatalCount:
        value.resolvedFatalCount as number,

      persistedFatalCount:
        value.persistedFatalCount as number,

      introducedFatalCount:
        value.introducedFatalCount as number,

      indeterminateControlFatalCount:
        value.indeterminateControlFatalCount as number,

      rootBoundary:
        normalizeRule(
          value.rootBoundary,
        ),

      horizontal:
        normalizeHorizontal(
          value.horizontal,
        ),
    });

  return Object.freeze({
    availability:
      'OBSERVED',

    observedAt:
      event.timestamp,

    geometryObservedAt:
      geometryEvent.timestamp,

    branchCounterfactualObservedAt:
      branchEvent.timestamp,

    geometryCoherenceDeltaMs:
      geometryDeltaMs,

    branchCounterfactualCoherenceDeltaMs:
      branchDeltaMs,

    coherentGeometryObservation:
      true,

    coherentBranchCounterfactualObservation:
      true,

    controlParity:
      true,

    controlWorldParity:
      true,

    controlFailureParity:
      true,

    cellBReferenceCoherent:
      true,

    cellA:
      Object.freeze({
        fatalCount:
          cellARaw.fatalCount as number,

        rootBoundaryCount:
          cellARaw.rootBoundaryCount as number,

        horizontalCount:
          cellARaw.horizontalCount as number,

        horizontal:
          Object.freeze(
            Object.fromEntries(
              LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
                name => [
                  name,
                  cellAHorizontal[
                    name
                  ] as number,
                ],
              ),
            ),
          ) as Readonly<
            Record<
              LegacyAncestryHorizontalRule,
              number
            >
          >,
      }),

    cellB:
      Object.freeze({
        availability:
          'OBSERVED',

        controlFatalCount:
          cellBRaw.controlFatalCount as number,

        shadowFatalCount:
          cellBRaw.shadowFatalCount as number,

        resolvedFatalCount:
          cellBRaw.resolvedFatalCount as number,

        persistedFatalCount:
          cellBRaw.persistedFatalCount as number,

        introducedFatalCount:
          cellBRaw.introducedFatalCount as number,

        indeterminateControlFatalCount:
          cellBRaw.indeterminateControlFatalCount as number,

        rootBoundary:
          normalizeRule(
            cellBRaw.rootBoundary,
          ),

        horizontal:
          normalizeHorizontal(
            cellBRaw.horizontal,
          ),
      }),

    cellC:
      normalizeCell(
        cellCRaw,
        cellARaw.fatalCount as number,
      ),

    cellD:
      normalizeCell(
        cellDRaw,
        cellARaw.fatalCount as number,
      ),

    semanticSubstitutedMemberCount:
      semanticSubstitutedMemberCount as number,

    semanticIndeterminateMemberCount:
      semanticIndeterminateMemberCount as number,

    layoutSubstitutedMemberCount:
      layoutSubstitutedMemberCount as number,

    layoutIndeterminateMemberCount:
      layoutIndeterminateMemberCount as number,

    sourceMutationAllowed:
      false,

    causalAttributionAllowed:
      false,

    repairAuthorized:
      false,

    legacyRetirementAuthorized:
      false,

    productionAuthorityChanged:
      false,

    readOnly:
      true,

    repairExecuted:
      false,

    identitySuppressed:
      true,
  });
};

export const useTreeRuntimeBranchLayoutInteractionEvidence =
  (): TreeBranchLayoutInteractionLiveProjection => {
    const revision =
      useSyncExternalStore(
        subscribeGovernorRuntimeEvidence,
        getGovernorRuntimeEvidenceRevision,
        getServerRevision,
      );

    return useMemo(
      () =>
        projectTreeRuntimeBranchLayoutInteractionEvidence(
          getGovernorRuntimeEvidenceSnapshot(),
        ),
      [revision],
    );
  };


export const useTreeRuntimeConvergenceEvidence =
  (): TreeRuntimeConvergenceLiveProjection => {
    const revision =
      useSyncExternalStore(
        subscribeGovernorRuntimeEvidence,
        getGovernorRuntimeEvidenceRevision,
        getServerRevision,
      );

    return useMemo(
      () => {
        const live =
          projectTreeRuntimeConvergenceEvidence(
            getGovernorRuntimeEvidenceSnapshot(),
          );

        if (live.observed) {
          return live;
        }

        return (
          readTreeRuntimeConvergenceSessionEvidence() ??
          live
        );
      },
      [revision],
    );
  };
